import React, { useState, useEffect } from 'react';
import './MyAccountPage.css';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { businessService } from '../../../shared/services/firebaseService';

const MyAccountPage = () => {
  const { user, businessData, refreshBusinessData } = useAuth();
  const [formData, setFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    businessCountry: '',
    companyDetails: '',
    pointsPerTap: 0,
    pointsForReward: 0,
    rewardDescription: '',
    brandColor: '',
    logoUrl: ''
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [tagActive, setTagActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load existing data from Firestore
    if (businessData) {
      setFormData({
        businessName: businessData.businessname || '',
        businessEmail: businessData.email || '',
        businessPhone: businessData.phone || '',
        businessCountry: businessData.country || '',
        companyDetails: '', // This field doesn't exist in our Firestore structure
        pointsPerTap: businessData.settings?.pointspertap || 10,
        pointsForReward: businessData.settings?.pointsforreward || 1200,
        rewardDescription: businessData.settings?.rewarddescription || 'a free reward',
        brandColor: businessData.settings?.brandcolour || '#FFA366',
        logoUrl: businessData.settings?.brandlogo || ''
      });
      
      // Set tag active status from first associated tag
      const firstTag = businessData.associatedtags ? Object.values(businessData.associatedtags)[0] : null;
      setTagActive(firstTag?.activate !== false);
      
      if (businessData.settings?.brandlogo) {
        setLogoPreview(businessData.settings.brandlogo);
      }
      
      setLoading(false);
    }
  }, [businessData]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        setFormData(prev => ({
          ...prev,
          logoUrl: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const updatedSettings = {
        brandlogo: formData.logoUrl,
        brandcolour: formData.brandColor,
        pointsforreward: formData.pointsForReward,
        pointspertap: formData.pointsPerTap,
        rewarddescription: formData.rewardDescription
      };
      
      await businessService.updateBusinessSettings(user.uid, updatedSettings);
      
      // Refresh business data from AuthContext
      await refreshBusinessData();
      
      alert(`Settings for ${formData.businessName} saved successfully!`);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTagToggle = () => {
    setTagActive(prev => !prev);
    console.log('Tag activation toggled:', !tagActive);
    // TODO: Implement API call to update tag status
  };

  // Function to darken a hex color for button text
  const darkenColor = (hex, amount = 0.4) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));
    
    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  };

  // Function to determine if a color is light or dark
  const isLightColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  // Calculate progress for preview (using example points since we don't have actual customer data)
  const examplePointsEarned = Math.floor(formData.pointsForReward * 0.7); // 70% progress for demo
  const progressPercentage = (examplePointsEarned / formData.pointsForReward) * 100;
  const textColor = isLightColor(formData.brandColor) ? '#666666' : 'white';

  // Horseshoe parameters for preview
  const centerX = 150;
  const centerY = 150;
  const radius = 135;
  const gapAngle = 70;
  const startAngle = 90 + (gapAngle / 2);
  const endAngle = 450 - (gapAngle / 2);
  const totalAngle = endAngle - startAngle;
  
  const startAngleRad = (startAngle * Math.PI) / 180;
  const endAngleRad = (endAngle * Math.PI) / 180;
  
  const startX = centerX + radius * Math.cos(startAngleRad);
  const startY = centerY + radius * Math.sin(startAngleRad);
  const endX = centerX + radius * Math.cos(endAngleRad);
  const endY = centerY + radius * Math.sin(endAngleRad);
  
  const horseshoePath = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`;
  
  const progressAngle = (progressPercentage / 100) * totalAngle;
  const progressEndAngleRad = (startAngle + progressAngle) * Math.PI / 180;
  const progressEndX = centerX + radius * Math.cos(progressEndAngleRad);
  const progressEndY = centerY + radius * Math.sin(progressEndAngleRad);
  
  const largeArcFlag = progressAngle > 180 ? 1 : 0;
  const progressPath = progressPercentage > 0 
    ? `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${progressEndX} ${progressEndY}`
    : '';

  if (loading) {
    return (
      <div className="myaccount-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading account settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="myaccount-page">
      <div className="myaccount-container">
        <h1 className="page-title">
          {formData.businessName ? `${formData.businessName} - Account Settings` : 'My Account Settings'}
        </h1>
        
        <div className="myaccount-content">
          {/* Form Section */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="logo" className="form-label">Business Logo</label>
              <div className="logo-upload-container">
                <input
                  type="file"
                  id="logo"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="logo-input"
                />
                <label htmlFor="logo" className="logo-upload-button">
                  {logoPreview ? (
                    <img src={logoPreview} alt={`${formData.businessName} logo`} className="logo-preview" />
                  ) : (
                    <div className="logo-placeholder">
                      <span>📷</span>
                      <span>{formData.businessName ? `${formData.businessName} Logo` : 'Upload Logo'}</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="businessName" className="form-label">Business Name</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pointsPerTap" className="form-label">Points Earned Per Tap</label>
                <input
                  type="number"
                  id="pointsPerTap"
                  name="pointsPerTap"
                  value={formData.pointsPerTap}
                  onChange={handleInputChange}
                  className="form-input"
                  min="1"
                  max="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="pointsForReward" className="form-label">Points for Reward</label>
                <input
                  type="number"
                  id="pointsForReward"
                  name="pointsForReward"
                  value={formData.pointsForReward}
                  onChange={handleInputChange}
                  className="form-input"
                  min="100"
                  max="10000"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rewardDescription" className="form-label">Reward Description</label>
              <input
                type="text"
                id="rewardDescription"
                name="rewardDescription"
                value={formData.rewardDescription}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="businessEmail" className="form-label">Business Email</label>
              <input
                type="email"
                id="businessEmail"
                name="businessEmail"
                value={formData.businessEmail}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessPhone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="businessPhone"
                  name="businessPhone"
                  value={formData.businessPhone}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="businessCountry" className="form-label">Country</label>
                <select
                  id="businessCountry"
                  name="businessCountry"
                  value={formData.businessCountry}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">Select Country</option>
                  {['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Other'].map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="companyDetails" className="form-label">Company Details</label>
              <textarea
                id="companyDetails"
                name="companyDetails"
                value={formData.companyDetails}
                onChange={handleInputChange}
                className="form-textarea"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="brandColor" className="form-label">Brand Color</label>
              <div className="color-input-container">
                <input
                  type="color"
                  id="brandColor"
                  name="brandColor"
                  value={formData.brandColor}
                  onChange={handleInputChange}
                  className="color-input"
                />
                <input
                  type="text"
                  value={formData.brandColor}
                  onChange={(e) => setFormData(prev => ({ ...prev, brandColor: e.target.value }))}
                  className="color-text-input"
                />
              </div>
            </div>

            <button onClick={handleSave} className="save-button" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            
            <button 
              onClick={handleTagToggle} 
              className={`tag-toggle-button ${tagActive ? 'active' : 'inactive'}`}
            >
              {tagActive ? 'Deactivate Tag' : 'Activate Tag'}
            </button>
          </div>

          {/* Preview Section */}
          <div className="preview-section">
            <h2 className="preview-title">
              {formData.businessName ? `${formData.businessName} Card Preview` : 'Card Preview'}
            </h2>
            <div className="card-preview-container">
              <div 
                className="reward-card-preview"
                style={{ backgroundColor: formData.brandColor }}
              >
                <div className="preview-header">
                  {logoPreview && (
                    <img src={logoPreview} alt="Business Logo" className="preview-logo" />
                  )}
                </div>

                <div className="preview-horseshoe-container">
                  <div className="preview-horseshoe-progress">
                    <svg className="preview-horseshoe-svg" viewBox="0 0 300 300">
                      <path
                        d={horseshoePath}
                        fill="none"
                        stroke={isLightColor(formData.brandColor) ? "rgba(102, 102, 102, 0.3)" : "rgba(255, 255, 255, 0.3)"}
                        strokeWidth="20"
                        strokeLinecap="round"
                      />
                      {progressPath && (
                        <path
                          d={progressPath}
                          fill="none"
                          stroke="url(#previewGradient)"
                          strokeWidth="20"
                          strokeLinecap="round"
                        />
                      )}
                      <defs>
                        <linearGradient id="previewGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor={isLightColor(formData.brandColor) ? "#666666" : "white"} />
                          <stop offset="100%" stopColor={isLightColor(formData.brandColor) ? "rgba(102, 102, 102, 0.8)" : "rgba(255, 255, 255, 0.8)"} />
                        </linearGradient>
                      </defs>
                    </svg>
                    
                    <div className="preview-points-display">
                      <div className="preview-points-text" style={{ color: textColor }}>
                        <span className="preview-earned-points">{examplePointsEarned}</span>
                        <span className="preview-separator"> of </span>
                        <span className="preview-total-points">{formData.pointsForReward}</span>
                      </div>
                      <div className="preview-points-label" style={{ color: textColor, opacity: 0.8 }}>points earned</div>
                    </div>
                  </div>
                </div>

                <div className="preview-reward-description">
                  <p style={{ color: textColor }}>
                    You're earning towards <strong style={{ color: textColor, opacity: 0.9 }}>{formData.rewardDescription}</strong> at <strong style={{ color: textColor, opacity: 0.9 }}>{formData.businessName}</strong>
                  </p>
                </div>

                <div className="preview-action-buttons">
                  <button 
                    className="preview-claim-button"
                    style={{ color: formData.brandColor ? darkenColor(formData.brandColor, 0.4) : '#B8732E' }}
                  >
                    {examplePointsEarned >= formData.pointsForReward ? 'CLAIM' : `${formData.pointsForReward - examplePointsEarned} points to go`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccountPage;
